import React from 'react';
import { Link } from 'react-router-dom';

/**
 * AI IMAGE PROMPTS FOR TESTIMONIALS:
 * 
 * student-1.jpg: "Professional headshot photo of confident young Indian male student (age 17-18), 
 * wearing casual shirt, warm smile, bright background, high quality portrait, 8K"
 * 
 * student-2.jpg: "Professional headshot photo of confident young Indian female student (age 17-18), 
 * wearing casual dress, genuine smile, bright background, high quality portrait, 8K"
 * 
 * student-3.jpg: "Professional headshot photo of determined young Indian male student (age 17-18), 
 * glasses, casual attire, confident expression, bright background, high quality portrait, 8K"
 * 
 * student-4.jpg: "Professional headshot photo of cheerful young Indian female student (age 17-18), 
 * traditional kurta, happy expression, bright background, high quality portrait, 8K"
 */

interface Testimonial {
  name: string;
  achievement: string;
  exam: string;
  rank: string;
  imagePlaceholder: string;
  quote: string;
  rating: number;
  location: string;
}

const TestimonialsV2: React.FC = () => {
  const testimonials: Testimonial[] = [
    {
      name: "Aarav Sharma",
      achievement: "IIT Bombay - Computer Science",
      exam: "JEE Advanced",
      rank: "AIR 142",
      imagePlaceholder: "student-1.jpg",
      quote: "WiziAI's AI performance analysis was a game-changer! It identified my weak topics in Physics that I wasn't even aware of. The personalized study plan and instant doubt solving helped me improve my score by 180 marks in just 4 months.",
      rating: 5,
      location: "Mumbai, Maharashtra"
    },
    {
      name: "Priya Patel",
      achievement: "AIIMS Delhi",
      exam: "NEET",
      rank: "AIR 95",
      imagePlaceholder: "student-2.jpg",
      quote: "The voice-enabled AI tutor was incredible! I could ask questions while studying late at night without disturbing anyone. WiziAI's detailed performance tracking kept me motivated and focused on high-yield topics throughout my preparation.",
      rating: 5,
      location: "Ahmedabad, Gujarat"
    },
    {
      name: "Rohit Kumar",
      achievement: "IIT Delhi - Mechanical",
      exam: "JEE Advanced",
      rank: "AIR 267",
      imagePlaceholder: "student-3.jpg",
      quote: "The adaptive mock tests were exactly what I needed. Each test challenged me at the right level, and the AI analysis after every test showed me exactly where to focus. The leaderboard feature made studying competitive and fun!",
      rating: 5,
      location: "Delhi NCR"
    },
    {
      name: "Sneha Gupta",
      achievement: "JIPMER Puducherry",
      exam: "NEET",
      rank: "AIR 342",
      imagePlaceholder: "student-4.jpg",
      quote: "WiziAI's comprehensive analytics helped me understand my preparation level clearly. The AI-generated study plan perfectly balanced all subjects, and the instant feedback on mock tests helped me learn from mistakes immediately.",
      rating: 5,
      location: "Bangalore, Karnataka"
    }
  ];

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <i
        key={i}
        className={`fas fa-star ${i < rating ? 'text-warning' : 'text-muted'}`}
        style={{ fontSize: '0.9rem' }}
      ></i>
    ));
  };

  return (
    <div className="testimonials-v2 py-5" 
      style={{ 
        background: 'linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%)'
      }}>
      <div className="container py-5">
        
        {/* Section Header */}
        <div className="text-center mb-5 pb-4">
          <div className="d-inline-flex align-items-center px-4 py-2 mb-3 rounded-pill"
            style={{
              background: 'linear-gradient(135deg, #f39c1215, #27ae6015)',
              border: '1px solid #f39c1230'
            }}>
            <i className="fas fa-quote-left me-2" style={{ color: '#f39c12' }}></i>
            <span className="fw-semibold" style={{ color: '#f39c12' }}>Early User Experiences</span>
          </div>
          <h2 className="display-4 fw-bold mb-3">
            What Our <span style={{ 
              background: 'linear-gradient(135deg, #f39c12, #27ae60)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>Beta Users Say</span>
          </h2>
          <p className="lead text-muted mb-0" style={{ maxWidth: '700px', margin: '0 auto' }}>
            Early feedback from students using WiziAI's AI-powered platform 
            during our beta testing phase.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="row g-4 mb-5">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="col-lg-6">
              <div className="card h-100 border-0 shadow-lg"
                style={{
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.1)';
                }}>
                
                {/* Card Header with Gradient */}
                <div className="card-header border-0 p-4"
                  style={{
                    background: index % 2 === 0 
                      ? 'linear-gradient(135deg, #667eea, #764ba2)'
                      : 'linear-gradient(135deg, #f39c12, #27ae60)'
                  }}>
                  <div className="d-flex align-items-center">
                    
                    {/* Student Image Placeholder */}
                    <div className="flex-shrink-0 me-3">
                      <div className="rounded-circle overflow-hidden"
                        style={{
                          width: '80px',
                          height: '80px',
                          border: '4px solid white'
                        }}>
                        <img 
                          src={`/img/home-v2/${testimonial.imagePlaceholder}`}
                          alt={testimonial.name}
                          className="w-100 h-100"
                          style={{ objectFit: 'cover' }}
                        />
                      </div>
                    </div>

                    {/* Student Info */}
                    <div className="flex-grow-1 text-white">
                      <h5 className="mb-1 fw-bold">{testimonial.name}</h5>
                      <p className="mb-1 small" style={{ opacity: 0.95 }}>
                        <i className="fas fa-graduation-cap me-1"></i>
                        {testimonial.achievement}
                      </p>
                      <div className="d-flex align-items-center gap-3">
                        {/* <span className="badge bg-white text-dark px-3 py-1">
                          <i className="fas fa-trophy me-1"></i>
                          {testimonial.rank}
                        </span> */}
                        <small style={{ opacity: 0.9 }}>
                          <i className="fas fa-map-marker-alt me-1"></i>
                          {testimonial.location}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body p-4">
                  {/* Rating */}
                  <div className="mb-3">
                    {renderStars(testimonial.rating)}
                  </div>

                  {/* Quote */}
                  <div className="position-relative">
                    <i className="fas fa-quote-left position-absolute text-muted" 
                      style={{ 
                        fontSize: '2rem', 
                        opacity: 0.1, 
                        top: '-10px', 
                        left: '-10px' 
                      }}></i>
                    <p className="text-muted mb-0 position-relative" style={{ paddingLeft: '1rem' }}>
                      "{testimonial.quote}"
                    </p>
                  </div>

                  {/* Exam Badge */}
                  <div className="mt-3 pt-3 border-top">
                    <span className="badge rounded-pill px-3 py-2"
                      style={{
                        background: index % 2 === 0 ? '#667eea15' : '#f39c1215',
                        color: index % 2 === 0 ? '#667eea' : '#f39c12',
                        border: index % 2 === 0 ? '2px solid #667eea' : '2px solid #f39c12'
                      }}>
                      <i className="fas fa-clipboard-check me-1"></i>
                      {testimonial.exam}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Stats Bar */}
        {/* <div className="rounded-4 p-5 mb-5"
          style={{
            background: 'linear-gradient(135deg, #667eea10, #764ba210)',
            border: '2px dashed #667eea50'
          }}>
          <div className="row g-4 text-center">
            <div className="col-md-3 col-sm-6">
              <div className="fw-bold display-5" style={{ color: '#667eea' }}>
                <i className="fas fa-brain me-2"></i>100K+
              </div>
              <p className="text-muted mb-0 fw-semibold">Questions Available</p>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="fw-bold display-5" style={{ color: '#f39c12' }}>
                <i className="fas fa-robot me-2"></i>24/7
              </div>
              <p className="text-muted mb-0 fw-semibold">AI Mentor Support</p>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="fw-bold display-5" style={{ color: '#27ae60' }}>
                <i className="fas fa-chart-line me-2"></i>100+
              </div>
              <p className="text-muted mb-0 fw-semibold">Analytics Metrics</p>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="fw-bold display-5" style={{ color: '#e74c3c' }}>
                <i className="fas fa-book me-2"></i>200+
              </div>
              <p className="text-muted mb-0 fw-semibold">Practice Tests</p>
            </div>
          </div>
        </div> */}

        {/* Bottom CTA */}
        <div className="text-center">
          <h3 className="fw-bold mb-4">Ready to Start Your Preparation Journey?</h3>
          <div className="d-flex flex-wrap gap-3 justify-content-center">
            <Link to="/signup" 
              className="btn btn-lg px-5 py-3 rounded-pill shadow-lg"
              style={{
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                border: 'none',
                color: 'white'
              }}>
              <i className="fas fa-rocket me-2"></i>
              Start Your Free Trial
            </Link>
            {/* <Link to="/success-stories" 
              className="btn btn-outline-primary btn-lg px-5 py-3 rounded-pill">
              <i className="fas fa-book-open me-2"></i>
              Read More Stories
            </Link> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestimonialsV2;
